import React, { useEffect, useRef, useState } from 'react';
import { TextField, Box } from '@mui/material';

const GooglePlacesAutocomplete = ({ value, onChange, onSelect, label, required, multiline, rows, ...props }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    // Get API key from environment - React apps need REACT_APP_ prefix
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
    
    // Debug: Log API key status (without exposing the full key)
    if (apiKey) {
      console.log('GooglePlacesAutocomplete: API key found', apiKey.substring(0, 10) + '...');
    } else {
      console.warn('GooglePlacesAutocomplete: REACT_APP_GOOGLE_PLACES_API_KEY is not set.');
      console.warn('GooglePlacesAutocomplete: Make sure you have set REACT_APP_GOOGLE_PLACES_API_KEY in your .env file and restarted the dev server.');
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('GooglePlacesAutocomplete: Google Maps API already loaded');
      setIsScriptLoaded(true);
      scriptLoadedRef.current = true;
      return;
    }

    // Check if script is already in the document
    let existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
    
    if (existingScript) {
      console.log('GooglePlacesAutocomplete: Script tag already exists, waiting for load...');
      
      // Script exists, check if it's loaded or wait for it
      const checkLoaded = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          console.log('GooglePlacesAutocomplete: Google Maps API ready');
          setIsScriptLoaded(true);
          scriptLoadedRef.current = true;
          return true;
        }
        return false;
      };

      // Check immediately
      if (checkLoaded()) {
        return;
      }

      // If not loaded yet, wait for it with polling
      const checkInterval = setInterval(() => {
        if (checkLoaded()) {
          clearInterval(checkInterval);
        }
      }, 100);

      // Also listen for load event
      const handleLoad = () => {
        console.log('GooglePlacesAutocomplete: Existing script onload fired');
        checkLoaded();
        clearInterval(checkInterval);
        if (existingScript) {
          existingScript.removeEventListener('load', handleLoad);
        }
      };

      if (existingScript) {
        existingScript.addEventListener('load', handleLoad);
      }

      // Cleanup after 10 seconds max
      setTimeout(() => {
        clearInterval(checkInterval);
        if (existingScript) {
          existingScript.removeEventListener('load', handleLoad);
        }
      }, 10000);

      return () => {
        clearInterval(checkInterval);
        if (existingScript) {
          existingScript.removeEventListener('load', handleLoad);
        }
      };
    } else {
      // Load the Google Maps JavaScript API script
      console.log('GooglePlacesAutocomplete: Loading Google Maps API script...');
      console.log('GooglePlacesAutocomplete: API key:', apiKey.substring(0, 10) + '...');
      
      // Create a unique callback name
      const callbackName = `googleMapsCallback_${Date.now()}`;
      
      // Set up the callback on window
      window[callbackName] = () => {
        console.log('GooglePlacesAutocomplete: Google Maps callback fired');
        if (window.google && window.google.maps && window.google.maps.places) {
          console.log('GooglePlacesAutocomplete: Google Maps API ready for use');
          setIsScriptLoaded(true);
          scriptLoadedRef.current = true;
          // Clean up callback
          delete window[callbackName];
        } else {
          console.error('GooglePlacesAutocomplete: Callback fired but API not available');
        }
      };
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.id = 'google-maps-script';
      
      const handleScriptLoad = () => {
        console.log('GooglePlacesAutocomplete: Script tag loaded (callback should fire soon)');
      };

      const handleScriptError = (error) => {
        console.error('GooglePlacesAutocomplete: Failed to load Google Maps JavaScript API');
        console.error('GooglePlacesAutocomplete: Error:', error);
        console.error('GooglePlacesAutocomplete: Please check your API key and ensure the Places API is enabled in your Google Cloud Console.');
        // Clean up callback on error
        if (window[callbackName]) {
          delete window[callbackName];
        }
      };

      script.onload = handleScriptLoad;
      script.onerror = handleScriptError;

      document.head.appendChild(script);
      console.log('GooglePlacesAutocomplete: Script tag added to document head with ID:', script.id);
      console.log('GooglePlacesAutocomplete: Full script URL:', script.src);

      // Don't remove script on cleanup - other components might use it
      return () => {
        scriptLoadedRef.current = false;
        // Clean up callback if component unmounts
        if (window[callbackName]) {
          delete window[callbackName];
        }
      };
    }
  }, []);

  useEffect(() => {
    if (isScriptLoaded && inputRef.current && window.google && window.google.maps && window.google.maps.places) {
      try {
        // Get the actual input element - Material-UI's inputRef gives us the input/textarea directly
        let inputElement = inputRef.current;
        
        // If multiline is enabled, Material-UI renders a textarea which Google Places Autocomplete doesn't support
        if (multiline) {
          console.warn('GooglePlacesAutocomplete: Cannot use autocomplete with multiline TextField (textarea). Google Places Autocomplete requires an input element. Please remove multiline prop.');
          return;
        }

        // Verify it's an input element
        if (!inputElement || inputElement.tagName !== 'INPUT') {
          console.error('GooglePlacesAutocomplete: inputRef does not point to an INPUT element. Got:', inputElement?.tagName || 'null');
          console.error('GooglePlacesAutocomplete: If using multiline TextField, Google Places Autocomplete will not work. Use a regular TextField instead.');
          return;
        }

        console.log('GooglePlacesAutocomplete: Initializing autocomplete on input element...', inputElement);
        
        // Helper function to check if dropdown has actual results or just footer
        const hasResults = (container) => {
          if (!container) return false;
          const pacItems = container.querySelectorAll('.pac-item');
          // Check if there are actual clickable items (not just the footer)
          // The footer is usually in a .pac-logo element
          const hasLogo = container.querySelector('.pac-logo') !== null;
          // If there are items OR no logo, we have results
          // If there's a logo but no items, it's just the footer
          return pacItems.length > 0 || !hasLogo;
        };

        // Track if we should allow dropdown to show
        let shouldShowDropdown = true;
        let selectionMade = false;

        // Ensure the dropdown container has proper styling when it appears
        const styleDropdown = () => {
          const pacContainer = document.querySelector('.pac-container');
          if (!pacContainer) return;
          
          // If selection was made, don't show dropdown
          if (selectionMade) {
            forceHideDropdown();
            return;
          }
          
          // Check if it's just the footer (no actual results) - hide if no results
          if (!hasResults(pacContainer)) {
            hideDropdown();
            return;
          }
          
          // Has results - show it and style it
          // Only style z-index and visibility, let Google handle positioning
          pacContainer.style.zIndex = '99999';
          pacContainer.style.display = 'block';
          pacContainer.style.visibility = 'visible';
          pacContainer.style.opacity = '1';
          pacContainer.style.pointerEvents = 'auto';
          pacContainer.style.height = 'auto';
          pacContainer.style.overflow = 'visible';
          
          // Make sure all pac-items are clickable
          const pacItems = pacContainer.querySelectorAll('.pac-item');
          pacItems.forEach(item => {
            item.style.pointerEvents = 'auto';
            item.style.cursor = 'pointer';
            // Ensure clicks propagate
            item.onclick = null; // Remove any onclick handlers we might have added
          });
        };

        // Initialize Google Places Autocomplete - requires HTMLInputElement
        autocompleteRef.current = new window.google.maps.places.Autocomplete(inputElement, {
          types: ['address'],
          fields: ['formatted_address', 'geometry', 'name'],
        });

        console.log('GooglePlacesAutocomplete: Autocomplete initialized successfully');
        
        // Style dropdown when it appears (Google Places adds it dynamically)
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.addedNodes.length) {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1 && (node.classList?.contains('pac-container') || node.querySelector?.('.pac-container'))) {
                  setTimeout(() => {
                    const pacContainer = document.querySelector('.pac-container');
                    if (pacContainer && !hasResults(pacContainer)) {
                      // If it's just the footer, hide it immediately
                      forceHideDropdown();
                    } else if (pacContainer) {
                      styleDropdown();
                    }
                  }, 50);
                }
              });
            }
            // Also check if items are removed (results cleared)
            if (mutation.type === 'childList') {
              setTimeout(() => {
                const pacContainer = document.querySelector('.pac-container');
                if (pacContainer && !hasResults(pacContainer)) {
                  forceHideDropdown();
                }
              }, 50);
            }
          });
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        // Also style it immediately and periodically in case it's already there or gets added
        styleDropdown();
        const styleInterval = setInterval(() => {
          const pacContainer = document.querySelector('.pac-container');
          if (!pacContainer) return;
          
          // If selection was made, hide it
          if (selectionMade) {
            forceHideDropdown();
            return;
          }
          
          // Check if it has results
          const hasActualResults = hasResults(pacContainer);
          
          // If no results, hide it
          if (!hasActualResults) {
            forceHideDropdown();
            return;
          }
          
          // If it has results and selection not made, show and style it
          if (hasActualResults && pacContainer.offsetParent !== null) {
            // Only style, don't hide
            pacContainer.style.zIndex = '99999';
            pacContainer.style.display = 'block';
            pacContainer.style.visibility = 'visible';
            pacContainer.style.opacity = '1';
            pacContainer.style.pointerEvents = 'auto';
          }
        }, 100);

        // Helper function to hide dropdown - more aggressive
        const hideDropdown = () => {
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer) {
            // Try multiple methods to ensure it's hidden
            pacContainer.style.display = 'none';
            pacContainer.style.visibility = 'hidden';
            pacContainer.style.opacity = '0';
            pacContainer.style.height = '0';
            pacContainer.style.overflow = 'hidden';
            // Also try removing it from DOM temporarily or hiding all children
            Array.from(pacContainer.children).forEach(child => {
              if (child.classList.contains('pac-logo')) {
                child.style.display = 'none';
              }
            });
          }
        };
        
        // Force hide dropdown - removes it if needed
        const forceHideDropdown = () => {
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer) {
            // Hide it multiple ways
            hideDropdown();
            // If it's still visible, try removing it
            if (pacContainer.offsetParent !== null) {
              pacContainer.style.display = 'none !important';
              pacContainer.remove();
            }
          }
        };

        // Listen for place selection
        const placeChangedListener = autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          console.log('GooglePlacesAutocomplete: Place changed event fired', place);
          
          // Mark that selection was made
          selectionMade = true;
          
          // Immediately hide dropdown when place_changed fires
          forceHideDropdown();
          
          // Also hide it multiple times with delays to catch any re-renders
          setTimeout(forceHideDropdown, 10);
          setTimeout(forceHideDropdown, 50);
          setTimeout(forceHideDropdown, 100);
          setTimeout(forceHideDropdown, 200);
          
          // Reset selection flag after a delay
          setTimeout(() => {
            selectionMade = false;
          }, 1000);
          
          // Check if we got a valid place
          if (!place || place.geometry === undefined) {
            console.warn('GooglePlacesAutocomplete: Invalid place result');
            return;
          }
          
          if (place.formatted_address) {
            console.log('GooglePlacesAutocomplete: Setting address:', place.formatted_address);
            setInputValue(place.formatted_address);
            if (onChange) {
              onChange({ target: { value: place.formatted_address } });
            }
            if (onSelect) {
              onSelect(place);
            }
          } else {
            console.warn('GooglePlacesAutocomplete: Place has no formatted_address');
          }
        });

        // Ensure clicks on pac-items work properly and hide dropdown when needed
        const ensureClickHandling = () => {
          const pacContainer = document.querySelector('.pac-container');
          if (!pacContainer) return;
          
          // Don't hide if selection was just made (let it happen naturally first)
          if (selectionMade) {
            forceHideDropdown();
            return;
          }
          
          // If container should be hidden (explicitly hidden or no results), hide it
          const explicitlyHidden = pacContainer.style.display === 'none' || 
                                   pacContainer.style.visibility === 'hidden';
          const noResults = !hasResults(pacContainer);
          
          if ((explicitlyHidden || noResults) && pacContainer.offsetParent !== null) {
            forceHideDropdown();
            return;
          }
          
          // If dropdown is visible and has results, make sure it's styled correctly
          if (pacContainer.offsetParent !== null && hasResults(pacContainer)) {
            pacContainer.style.pointerEvents = 'auto';
            // Make sure all pac-items are clickable
            const pacItems = pacContainer.querySelectorAll('.pac-item');
            pacItems.forEach(item => {
              item.style.pointerEvents = 'auto';
              item.style.cursor = 'pointer';
            });
          }
        };

        // Check for pac-items periodically and ensure they're clickable
        const clickCheckInterval = setInterval(ensureClickHandling, 50);
        
        // Only hide dropdown when there are no results or selection was made
        const aggressiveHideInterval = setInterval(() => {
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer && pacContainer.offsetParent !== null) {
            // If selection was made, always hide
            if (selectionMade) {
              forceHideDropdown();
              return;
            }
            
            // Only hide if no results - if there ARE results, let it show
            if (!hasResults(pacContainer)) {
              forceHideDropdown();
            }
            // If hasResults returns true, do nothing - let dropdown show
          }
        }, 150);

        // Listen for when dropdown opens to ensure it's styled
        inputElement.addEventListener('focus', () => {
          selectionMade = false; // Reset on focus
          // Give Google time to create the dropdown before styling
          setTimeout(() => {
            styleDropdown();
          }, 200);
        });
        
        // Reset selection flag when user starts typing
        inputElement.addEventListener('input', (e) => {
          selectionMade = false; // Reset on input - user is typing new text
          // Give Google time to populate results
          setTimeout(() => {
            styleDropdown();
          }, 300);
        });
        
        // Hide dropdown when clicking outside
        const handleClickOutside = (e) => {
          const pacContainer = document.querySelector('.pac-container');
          const target = e.target;
          // Check if click is outside both the input and the dropdown
          if (pacContainer && 
              !pacContainer.contains(target) && 
              !inputElement.contains(target) &&
              target !== inputElement) {
            forceHideDropdown();
          }
        };
        
        // Hide dropdown when input loses focus
        const handleBlur = () => {
          setTimeout(() => {
            forceHideDropdown();
            // Also check again after a delay
            setTimeout(forceHideDropdown, 100);
          }, 150);
        };
        
        // Hide dropdown when input value changes and there are no results
        const handleInput = (e) => {
          // Track last input time for aggressive hiding
          inputElement.dataset.lastInputTime = Date.now();
          
          setTimeout(() => {
            const pacContainer = document.querySelector('.pac-container');
            if (pacContainer) {
              // If no results, hide it
              if (!hasResults(pacContainer)) {
                forceHideDropdown();
              }
            }
          }, 200);
        };
        
        document.addEventListener('click', handleClickOutside, true);
        inputElement.addEventListener('blur', handleBlur);
        inputElement.addEventListener('input', handleInput);
        inputElement.addEventListener('keydown', (e) => {
          // Hide on Escape key
          if (e.key === 'Escape') {
            forceHideDropdown();
          }
        });

        return () => {
          // Cleanup autocomplete
          if (autocompleteRef.current && window.google && window.google.maps) {
            window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            autocompleteRef.current = null;
          }
          if (observer) {
            observer.disconnect();
          }
          if (styleInterval) {
            clearInterval(styleInterval);
          }
          if (clickCheckInterval) {
            clearInterval(clickCheckInterval);
          }
          if (aggressiveHideInterval) {
            clearInterval(aggressiveHideInterval);
          }
          if (placeChangedListener) {
            window.google?.maps?.event?.removeListener(placeChangedListener);
          }
          inputElement.removeEventListener('focus', styleDropdown);
          inputElement.removeEventListener('blur', handleBlur);
          inputElement.removeEventListener('input', handleInput);
          document.removeEventListener('click', handleClickOutside);
          // Final cleanup - remove dropdown if it exists
          forceHideDropdown();
        };
      } catch (error) {
        console.error('GooglePlacesAutocomplete: Error initializing autocomplete', error);
      }
    } else {
      if (!isScriptLoaded) {
        console.log('GooglePlacesAutocomplete: Waiting for script to load...');
      }
      if (!inputRef.current) {
        console.log('GooglePlacesAutocomplete: Waiting for input ref...');
      }
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('GooglePlacesAutocomplete: Waiting for Google Maps API...');
      }
    }
  }, [isScriptLoaded, onChange, onSelect]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // If input is cleared, force hide dropdown
    if (!newValue.trim()) {
      setTimeout(() => {
        const pacContainer = document.querySelector('.pac-container');
        if (pacContainer) {
          pacContainer.style.display = 'none';
          pacContainer.style.visibility = 'hidden';
          pacContainer.style.opacity = '0';
          pacContainer.remove();
        }
      }, 50);
    }
    
    // Don't call onChange here to avoid conflicts with Google Places selection
    // The place_changed event will handle the value update
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <style>
        {`
          .pac-container {
            z-index: 99999 !important;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3) !important;
            font-family: inherit !important;
            background-color: #fff !important;
            border: 1px solid #ccc !important;
            pointer-events: auto !important;
          }
          /* Hide the "powered by Google" footer */
          .pac-container .pac-logo,
          .pac-container .pac-logo:after {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            width: 0 !important;
          }
          /* Hide dropdown if it only contains the logo */
          .pac-container:has(.pac-logo):not(:has(.pac-item)) {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          .pac-item {
            padding: 10px 12px !important;
            cursor: pointer !important;
            border-top: 1px solid #e6e6e6 !important;
            font-size: 14px !important;
          }
          .pac-item:first-child {
            border-top: none !important;
          }
          .pac-item:hover {
            background-color: #f5f5f5 !important;
          }
          .pac-item-selected {
            background-color: #e8f0fe !important;
          }
          .pac-matched {
            font-weight: bold !important;
          }
          .pac-icon {
            margin-right: 8px !important;
          }
          .MuiDrawer-paper {
            overflow: visible !important;
          }
        `}
      </style>
      <TextField
        {...props}
        inputRef={inputRef}
        label={label}
        value={inputValue}
        onChange={handleInputChange}
        required={required}
        multiline={false}
        rows={rows}
        fullWidth
      />
    </Box>
  );
};

export default GooglePlacesAutocomplete;

